import { resolveDateFormat } from './dateFormats';
import dayjs, { browserTimeZone, preferredTimeZone } from './dayjs';
import newDateFromString from '../../shared/dateHelper';

import type { Dayjs } from 'dayjs';

type DateValue = string | number | Date | Dayjs | null | undefined;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_MONTH_PATTERN = /^\d{4}-\d{2}$/;
const YEAR_ONLY_PATTERN = /^\d{4}$/;

const toDayjs = (value: DateValue): Dayjs | null => {
  if (value == null || value === '') return null;

  let parsed: Dayjs;

  if (dayjs.isDayjs(value)) {
    parsed = value;
  } else if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    parsed = dayjs(value);
  } else if (typeof value === 'string' && YEAR_MONTH_PATTERN.test(value)) {
    parsed = dayjs(value, 'YYYY-MM');
  } else if (typeof value === 'string' && YEAR_ONLY_PATTERN.test(value)) {
    parsed = dayjs(value, 'YYYY');
  } else {
    parsed = dayjs(typeof value === 'string' ? newDateFromString(value) : value);
  }

  return parsed.isValid() ? parsed : null;
};

export const formatDate = (value: DateValue, format: string, fallback = ''): string => {
  const parsed = toDayjs(value);
  return parsed ? parsed.format(resolveDateFormat(format)) : fallback;
};

export const formatInTimeZone = (
  value: DateValue,
  timeZone: string,
  format: string,
  fallback = ''
): string => {
  const parsed = toDayjs(value);
  return parsed ? parsed.tz(timeZone).format(resolveDateFormat(format)) : fallback;
};

export const formatInUserTimeZone = (value: DateValue, format: string, fallback = ''): string =>
  formatInTimeZone(value, preferredTimeZone, format, fallback);

export const formatInBrowserTimeZone = (value: DateValue, format: string, fallback = ''): string =>
  formatInTimeZone(value, browserTimeZone, format, fallback);

export const formatRelativeTime = (value: DateValue, fallback = ''): string => {
  const parsed = toDayjs(value);
  return parsed ? parsed.fromNow() : fallback;
};

export const differenceInHours = (left: DateValue, right: DateValue): number => {
  const parsedLeft = toDayjs(left);
  const parsedRight = toDayjs(right);

  if (!parsedLeft || !parsedRight) return 0;

  return parsedLeft.diff(parsedRight, 'hour', true);
};

export { preferredTimeZone };
